<template>
  <Loader :show="loadingFilters" v-if="loadingFilters"></Loader>
  <div v-else>
    <MDBRow>
      <MDBCol col="5" offsetLg="7">
        <div class="d-flex flex-column justify-content-end">
          <div>
            <input
              v-model="searchText"
              type="text"
              class="form-control"
              :placeholder="$t('brochures.searchMedia')"
              :disabled="isInteractionLocked"
            />
          </div>
          <span class="d-flex justify-content-end">
            <MDBBtn color="primary" @click="scheduleFilter({ resetPage: true })" :disabled="isInteractionLocked">{{
              $t("global.buttons.toSearch")
            }}</MDBBtn>
          </span>
        </div>
      </MDBCol>
    </MDBRow>

    <MDBRow class="pb-3 pt-3">
      <MDBCol col="12" v-html="$t('global.buttons.setFilters')">
      </MDBCol>
    </MDBRow>

    <MDBRow v-if="categories.length > 0">
      <MDBCol cols="12" lg="6" xl="2" class="mb-4" v-for="(data, i) in categories" :key="i">
        <div class="d-flex justify-content-start align-items-center pb-3">
          <span class="fw-bold">{{ $t("media.mediaCategories." + data.slug) }}</span>
        </div>

        <div>
          <div class="d-flex justify-content-start align-items-center" v-for="child in data.children" :key="child.id">
            <MDBSwitch
              @input="filterFiles(child.id, child.parent_id)"
              v-model="categorySelections[child.id]"
              :disabled="isInteractionLocked"
            />
            <span>{{ $t("media.mediaCategories." + child.slug) }}</span>
          </div>
        </div>
      </MDBCol>
      <MDBCol cols="12"><span @click="clearFilters()" class="text-decoration-underline cursor--pointer" :class="{ 'opacity-50': isInteractionLocked }">{{
        $t("global.buttons.clearFilters") }}
        </span></MDBCol>
    </MDBRow>

    <Loader :show="showResultsLoader" v-if="showResultsLoader"></Loader>
    <div v-else class="pt-5">
      <MDBRow class="pb-3">
        <MDBCol cols="12" lg="6"><span class="fw-bold">{{ $t("brochures.searchResults") }}:</span>
          {{ totalMediaFiles }}</MDBCol>
        <MDBCol cols="12" lg="6" class="d-flex justify-content-end"><span
            class="me-2 cursor--pointer text-decoration-underline" @click="selectAll()">{{
              $t("global.buttons.selectAll")
            }}</span>
          <span @click="deselectAll()" class="cursor--pointer text-decoration-underline">{{
            $t("global.buttons.deselectAll")
            }}</span>
        </MDBCol>
      </MDBRow>
      <MDBRow class="pb-3">
        <MDBCol cols="12" class="d-flex justify-content-end">
          <MDBBtn type="button" color="danger" @click="downloadAll()" :disabled="!selectedItemIds.length">{{
            $t("global.buttons.downloadSelection") }}</MDBBtn>
        </MDBCol>
      </MDBRow>
      <MDBRow class="pb-5">
        <MDBCol cols="12" lg="6">{{ $t("global.sortBy.choiceBy") }}:</MDBCol>
        <MDBCol cols="12" lg="6" class="d-flex justify-content-end"><span>{{ $t("global.sortBy.date") }}
            <img @click="sortData('created_at', 'desc')" :src="require('@/assets/img/icons/arrow_up_black.png')"
              alt="filter_btn" class="cursor--pointer" />
            <img @click="sortData('created_at', 'asc')" :src="require('@/assets/img/icons/arrow_down_black.png')"
              alt="filter_btn" class="cursor--pointer" /></span>
          <span>{{ $t("global.sortBy.name") }}
            <img @click="sortData('name', 'desc')" :src="require('@/assets/img/icons/arrow_up_black.png')"
              alt="filter_btn" class="cursor--pointer" />
            <img @click="sortData('name', 'asc')" :src="require('@/assets/img/icons/arrow_down_black.png')"
              alt="filter_btn" class="cursor--pointer" /></span>
        </MDBCol>
      </MDBRow>
      <MDBRow v-if="mediaFiles && mediaFiles.length > 0">
        <MDBCol cols="12" md="6" xl="4" class="mb-4" v-for="media in mediaFiles" :key="media.id">
          <div class="h-100 mx-2 media-file-card">
            <DocumentPreview
              :file="media"
              :selected="isItemSelected(media.id)"
              @toggle="selectItems"
            >
              <template #actions>
                <template v-if="!media.embeded">
                  <span
                    @click.stop="dowloadSingleFile(media.id)"
                    class="text-decoration-underline cursor--pointer"
                  >Download</span>
                  |
                  <a :href="media.file" target="_blank" rel="noopener noreferrer" class="text-danger text-decoration-underline cursor--pointer">{{
                    $t("global.buttons.preview") }}</a>
                </template>
                <a
                  v-else
                  :href="media.embeded"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-danger text-decoration-underline cursor--pointer"
                >{{ $t("global.buttons.preview") }}</a>
              </template>
            </DocumentPreview>
          </div>
        </MDBCol>
      </MDBRow>

      <MDBRow v-if="paginationLinks && mediaFiles && mediaFiles.length > 0">
        <MDBCol cols="12" class="d-flex justify-content-center">
          <MDBPagination>
            <MDBPageItem v-for="(paginationLink, index) in paginationLinks" :key="index"><span class="cursor--pointer"
                :class="`${!paginationLink.active ? '' : 'text-decoration-underline'
                  }`" v-html="paginationLink.label" @click="paginate(paginationLink.url)"></span></MDBPageItem>
          </MDBPagination>
        </MDBCol>
      </MDBRow>
    </div>
  </div>
</template>

<script setup>
import Loader from "@/components/General/Loader/index.vue";
import DocumentPreview from "@/components/DocumentPreview/DocumentPreview.vue";
import {
  MDBRow,
  MDBCol,
  MDBSwitch,
  MDBBtn,
  MDBPagination,
  MDBPageItem,
} from "mdb-vue-ui-kit";
import { onMounted, onBeforeUnmount, computed, ref, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { MediaService } from "@/services/media.service";

import { useStore } from "vuex";

const store = useStore();
const route = useRoute();
const router = useRouter();

const categories = computed(() => store.getters.getMediaCategories);
const loading = computed(() => store.getters.getPending);
const mediaFiles = computed(() => store.getters.getMediaFiles);
const paginationLinks = computed(() => store.getters.getPaginationLinks);
const totalMediaFiles = computed(() => store.getters.getTotalMediaFiles);

const selectedItemIds = ref([]);
const selectedCategories = ref([]);
const categorySelections = ref([]);
const loadingFilters = ref(true);
const searchText = ref(null);
const page = ref(1);
const sortField = ref("created_at");
const sortType = ref("desc");
const filterDebounceTimeout = ref(null);
const isFiltering = ref(false);
const isInteractionLocked = computed(() => loadingFilters.value || isFiltering.value);
const showResultsLoader = computed(() => loading.value || isFiltering.value);

const getCategoryChildren = () =>
  (categories.value || []).flatMap((category) => category.children || []);

const getQueryList = (value) => {
  if (!value) {
    return [];
  }

  const parts = Array.isArray(value) ? value : String(value).split(",");
  return parts.map((part) => String(part).trim()).filter(Boolean);
};

const getFiltersFromQuery = () => getQueryList(route.query.filters);

const getSearchFromQuery = () => {
  const search = Array.isArray(route.query.search)
    ? route.query.search[0]
    : route.query.search;

  if (!search) {
    return null;
  }

  const normalized = String(search).trim();
  return normalized || null;
};

const getPageFromQuery = () => {
  const pageParam = Number(route.query.page);
  return pageParam > 0 ? pageParam : 1;
};

const getQueryValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(",");
  }

  return value ? String(value) : "";
};

const getSlugsFromSelectedCategories = () => {
  const children = getCategoryChildren();

  return selectedCategories.value
    .map((item) => {
      const [id, parentId] = String(item).split("_");
      const child = children.find(
        (categoryChild) =>
          String(categoryChild.id) === String(id) &&
          String(categoryChild.parent_id) === String(parentId)
      );

      return child?.slug || item;
    })
    .filter(Boolean);
};

const isChildSelectedByQuery = (child, queryFilters) => {
  const token = `${child.id}_${child.parent_id}`;
  const parent = (categories.value || []).find(
    (category) => String(category.id) === String(child.parent_id)
  );

  return (
    queryFilters.includes(child.slug) ||
    queryFilters.includes(token) ||
    queryFilters.includes(String(child.id)) ||
    (parent?.slug && queryFilters.includes(parent.slug))
  );
};

const resetCategorySelections = (selectedIds = new Set()) => {
  const selections = [];

  for (const child of getCategoryChildren()) {
    selections[child.id] = selectedIds.has(child.id);
  }

  categorySelections.value = selections;
};

const isApplyingRoute = ref(false);

const applyRouteQueryToLocalState = () => {
  const queryFilters = getFiltersFromQuery();
  const selectedIds = new Set();

  selectedCategories.value = [];

  for (const child of getCategoryChildren()) {
    if (!isChildSelectedByQuery(child, queryFilters)) {
      continue;
    }

    selectedIds.add(child.id);
    selectedCategories.value.push(`${child.id}_${child.parent_id}`);
  }

  resetCategorySelections(selectedIds);
  searchText.value = getSearchFromQuery();
  page.value = getPageFromQuery();
};

const getSelectedTokensFromQuery = () => {
  const queryFilters = getFiltersFromQuery();

  return getCategoryChildren()
    .filter((child) => isChildSelectedByQuery(child, queryFilters))
    .map((child) => `${child.id}_${child.parent_id}`);
};

const localStateMatchesQuery = () => {
  const selectedFromQuery = getSelectedTokensFromQuery();
  const sameFilters =
    selectedFromQuery.length === selectedCategories.value.length &&
    selectedFromQuery.every((item) => selectedCategories.value.includes(item));

  return (
    sameFilters &&
    (searchText.value || "").trim() === (getSearchFromQuery() || "") &&
    Number(page.value) === getPageFromQuery()
  );
};

const buildQueryFromState = () => {
  const query = { ...route.query };
  const slugs = getSlugsFromSelectedCategories();
  const search =
    typeof searchText.value === "string" ? searchText.value.trim() : "";
  const pageNumber = Number(page.value);

  if (slugs.length) {
    query.filters = slugs.join(",");
  } else {
    delete query.filters;
  }

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (pageNumber > 1) {
    query.page = String(pageNumber);
  } else {
    delete query.page;
  }

  return query;
};

const updateRouteQuery = async () => {
  const query = buildQueryFromState();

  if (
    getQueryValue(query.filters) === getQueryValue(route.query.filters) &&
    getQueryValue(query.search) === getQueryValue(route.query.search) &&
    getQueryValue(query.page) === getQueryValue(route.query.page)
  ) {
    return;
  }

  await router.replace({ query });
};

const filterFiles = (id, parentId) => {
  if (isInteractionLocked.value || isApplyingRoute.value) {
    return;
  }

  // If the ID already exists, remove it
  const data = `${id}_${parentId}`
  const existingIndex = selectedCategories.value.indexOf(data);
  if (existingIndex !== -1) {
    selectedCategories.value.splice(existingIndex, 1);
  } else {
    selectedCategories.value.push(data);
  }

  scheduleFilter({ resetPage: true });
};

const sortData = (field, type) => {
  if (isInteractionLocked.value) {
    return;
  }

  sortField.value = field;
  sortType.value = type;

  scheduleFilter({ resetPage: true });
};

const filter = async () => {
  if (isFiltering.value) {
    return;
  }

  try {
    deselectAll();
    const data = {
      filters: selectedCategories.value,
      searchText: searchText.value,
      page: page.value,
      sortField: sortField.value,
      sortType: sortType.value,
      itemsPerPage: 9
    };
    isFiltering.value = true;
    await store.dispatch("filterMediaFiles", data);
  } finally {
    isFiltering.value = false;
  }
};

const scheduleFilter = ({ resetPage = false } = {}) => {
  if (resetPage) {
    page.value = 1;
  }

  updateRouteQuery();

  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }

  filterDebounceTimeout.value = setTimeout(() => {
    filter();
  }, 1000);
};

const paginate = async (label) => {
  if (isInteractionLocked.value || label === null) {
    return;
  }

  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }

  page.value = label.match(/\d*$/g)[0];
  await updateRouteQuery();

  await filter();
};

const selectItems = (media) => {
  const existingIndex = selectedItemIds.value.indexOf(media.id);
  if (existingIndex !== -1) {
    selectedItemIds.value.splice(existingIndex, 1);
  } else {
    selectedItemIds.value.push(media.id);
  }
};

const isItemSelected = (id) => {
  const existingIndex = selectedItemIds.value.indexOf(id);
  return existingIndex !== -1;
};

const downloadFile = async () => {
  const response = await MediaService.getZippedFiles(selectedItemIds.value);
  window.open(response.data);
};

const selectAll = () => {
  deselectAll();
  for (let media of mediaFiles.value) {
    selectItems(media);
  }
};

const downloadAll = () => {
  downloadFile(selectedItemIds.value);
};

const dowloadSingleFile = (id) => {
  selectedItemIds.value = [];
  selectedItemIds.value.push(id);
  downloadFile(selectedItemIds.value);
};

const deselectAll = () => {
  selectedItemIds.value = [];
};

onMounted(async () => {
  loadingFilters.value = true;
  isApplyingRoute.value = true;

  try {
    await store.dispatch("fetchMediaCategories");
    applyRouteQueryToLocalState();
    if (selectedCategories.value.length) {
      await updateRouteQuery();
    }
    await filter();
    loadingFilters.value = false;
    await nextTick();
  } finally {
    isApplyingRoute.value = false;
  }
});

watch(
  () => [route.query.filters, route.query.search, route.query.page],
  async () => {
    if (route.name !== "media" || localStateMatchesQuery()) {
      return;
    }

    if (filterDebounceTimeout.value) {
      clearTimeout(filterDebounceTimeout.value);
    }

    isApplyingRoute.value = true;

    try {
      applyRouteQueryToLocalState();
      await nextTick();
      await filter();
    } finally {
      isApplyingRoute.value = false;
    }
  }
);

onBeforeUnmount(() => {
  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }
});

const clearFilters = async () => {
  if (isFiltering.value) {
    return;
  }

  loadingFilters.value = true;
  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }
  selectedItemIds.value = [];
  selectedCategories.value = [];
  page.value = 1;
  searchText.value = null;

  await store.dispatch("fetchMediaCategories");
  resetCategorySelections();
  await updateRouteQuery();
  await filter();
  loadingFilters.value = false;
};
</script>

<style scoped lang="scss">
@import "@/styles/media_content.scss";
</style>
